import Median from 'median-js-bridge'

// True inside the Median native app.
export function inMedianApp() {
  if (typeof navigator !== 'undefined' && /median|gonative/i.test(navigator.userAgent || '')) return true
  if (typeof window !== 'undefined' && window.median) return true
  try { return !!(Median.isNativeApp && Median.isNativeApp()) } catch { return false }
}

// Generic plain-text share, for callers that just need "send this text
// somewhere" (no image attachment, unlike shareChurchInvite below). Tries
// Median's native share bridge first (navigator.share often can't open the
// share sheet inside the Median WebView), then the Web Share API, then
// falls back to a clipboard copy — the caller decides how to surface that
// ("Copied" toast, alert, etc.) via onCopied.
export async function shareTextViaBestChannel(text, onCopied) {
  try {
    if (inMedianApp() && Median.share && Median.share.sharePage) {
      Median.share.sharePage({ text })
      return
    }
  } catch (_e) { /* fall through */ }

  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ text })
      return
    }
  } catch (_e) { /* user cancelled or unsupported — fall through */ }

  try {
    await navigator.clipboard.writeText(text)
    onCopied?.()
  } catch {
    // eslint-disable-next-line no-alert
    window.prompt('Copy this:', text)
  }
}

// "Invite your church leader" — opens the device share sheet (text / email /
// WhatsApp / …) with a message nudging a leader to set their church up on The
// Bible Community. Used from the onboarding church steps and the in-app church
// picker when a user can't find their church.
export const INVITE_LEADER_URL  = 'https://thebiblecommunity.co.uk'
// Same-origin path to the app logo (green Bible icon) shared as the preview image.
const INVITE_LEADER_LOGO = '/apple-touch-icon.png'
// Plain text — share targets (SMS / WhatsApp / email) don't render HTML, so we
// can't bold words here; kept as a clean single message.
export const INVITE_LEADER_TEXT =
  `I'm inviting you to set up our church on The Bible Community.\n\n` +
  `Every Sunday sermon becomes a 5-day devotional your church can follow through the week, with sermon notes and video links on every verse, plus prayer and community features :)`

export async function shareChurchInvite() {
  const full = `${INVITE_LEADER_TEXT}\n\n${INVITE_LEADER_URL}`

  // Preferred path: attach the app logo as an image file so the share sheet
  // preview shows the logo (the native picker doesn't fetch a URL's og:image —
  // that's only used by receiving apps when unfurling the link afterwards).
  try {
    if (typeof navigator !== 'undefined' && navigator.canShare && navigator.share) {
      const res  = await fetch(INVITE_LEADER_LOGO)
      const blob = await res.blob()
      const file = new File([blob], 'the-bible-community.png', { type: blob.type || 'image/png' })
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'The Bible Community', text: full })
        return
      }
    }
  } catch (_e) { /* image fetch / share unsupported — fall through */ }

  // Native Median share sheet.
  try {
    if (inMedianApp() && Median.share && Median.share.sharePage) {
      Median.share.sharePage({ url: INVITE_LEADER_URL, text: INVITE_LEADER_TEXT })
      return
    }
  } catch (_e) { /* fall through */ }

  // Web Share API without files (older platforms).
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title: 'The Bible Community', text: INVITE_LEADER_TEXT, url: INVITE_LEADER_URL })
      return
    }
  } catch (_e) { /* user cancelled or unsupported — fall through */ }

  // Last resort: copy to clipboard.
  try {
    await navigator.clipboard.writeText(full)
    // eslint-disable-next-line no-alert
    alert('Invite copied — paste it into a text, email or WhatsApp message.')
  } catch {
    // eslint-disable-next-line no-alert
    window.prompt('Copy this invite:', full)
  }
}
